import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "../ui/Modal";
import * as XLSX from "xlsx";
import {
  fetchSideMenuItems,
  addSideMenuItem,
  editSideMenuItem,
  deleteSideMenuItem,
} from "../../services/menuApi";
import "./UniversalSidesModal.css";

const emptyForm = {
  id: "",
  item: "",
  price: "0.00",
  description: "",
  quantity: "100",
  visibility: true,
};

export default function UniversalSidesModal({
  open,
  tenantServerUrl,
  onClose,
  onDownloadTemplate,
  onImport,
}) {
  const [sides, setSides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!tenantServerUrl) return;
    setLoading(true);
    setErr("");
    try {
      const rows = await fetchSideMenuItems(tenantServerUrl, { universal: true });
      setSides(rows);
    } catch (e) {
      setErr(e?.message || "Failed to load universal sides");
    } finally {
      setLoading(false);
    }
  }, [tenantServerUrl]);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm);
    setEditingId(null);
    setErr("");
    load();
  }, [open, load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const startEdit = (side) => {
    setEditingId(side.id);
    setForm({
      id: side.id,
      item: side.item || side.name || "",
      price: String(side.price ?? "0"),
      description: side.description || "",
      quantity: String(side.quantity ?? "100"),
      visibility: String(side.visibility) !== "0",
    });
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const name = String(form.item || "").trim();
    if (!name) {
      setErr("Side name is required");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const payload = {
        item: name,
        price: String(form.price ?? "0").trim() || "0",
        description: String(form.description || "").trim(),
        quantity: parseInt(String(form.quantity || "0").replace(/\D/g, ""), 10) || 0,
        subcode_id: 0,
        visibility: form.visibility ? 1 : 0,
        universal: 1,
      };
      if (editingId) {
        await editSideMenuItem(tenantServerUrl, { ...payload, id: editingId });
      } else {
        await addSideMenuItem(tenantServerUrl, payload);
      }
      resetForm();
      await load();
    } catch (ex) {
      setErr(ex?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (side) => {
    const ok = window.confirm(
      `Delete universal side "${side.item}"?\nIt will be removed from any menu items that use it.`
    );
    if (!ok) return;
    setSaving(true);
    setErr("");
    try {
      await deleteSideMenuItem(tenantServerUrl, { id: side.id });
      if (editingId === side.id) resetForm();
      await load();
    } catch (ex) {
      setErr(ex?.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const handleImportClick = () => fileRef.current?.click();

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !onImport) return;
    try {
      setSaving(true);
      setErr("");
      await onImport(file);
      await load();
    } catch (ex) {
      setErr(ex?.message || "Import failed");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    if (!sides.length) {
      setErr("No sides to export");
      return;
    }
    const rows = sides.map((side) => ({
      item: side.item,
      price: side.price,
      description: side.description,
      quantity: side.quantity,
      visibility: side.visibility,
      universal: side.universal ?? 1,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Sides");
    XLSX.writeFile(wb, "menu_sides.xlsx");
  };

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose?.()}
      title="Universal Sides"
      width={860}
      footer={
        <div className="universal-sides-footer">
          <button type="button" className="universal-sides-btn" onClick={onClose} disabled={saving}>
            Close
          </button>
        </div>
      }
    >
      <p className="universal-sides-help">
        Universal sides are created once and reused across menu items in any category,
        so you do not have to recreate fries, salad, and similar extras on every item.
      </p>

      <div className="universal-sides-file-actions">
        {onDownloadTemplate && (
          <button
            type="button"
            className="universal-sides-btn"
            onClick={onDownloadTemplate}
            disabled={saving}
          >
            Template
          </button>
        )}
        {onImport && (
          <>
            <button
              type="button"
              className="universal-sides-btn"
              onClick={handleImportClick}
              disabled={saving}
            >
              Import
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".json,.csv,.xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleImportFile}
            />
          </>
        )}
        <button
          type="button"
          className="universal-sides-btn"
          onClick={handleExport}
          disabled={saving || loading}
        >
          Export
        </button>
      </div>

      {!!err && <div className="universal-sides-error">{err}</div>}

      <form className="universal-sides-form" onSubmit={submit}>
        <label>
          <span>Name</span>
          <input
            value={form.item}
            onChange={(e) => set("item", e.target.value)}
            placeholder="e.g., Onion Rings"
          />
        </label>
        <label>
          <span>Price</span>
          <input
            value={form.price}
            onChange={(e) => set("price", e.target.value.replace(/[^\d.]/g, ""))}
            placeholder="0.00"
          />
        </label>
        <label>
          <span>Stock qty</span>
          <input
            value={form.quantity}
            onChange={(e) => set("quantity", e.target.value.replace(/\D/g, ""))}
            placeholder="100"
          />
        </label>
        <label className="universal-sides-visible">
          <span>Visible</span>
          <input
            type="checkbox"
            checked={!!form.visibility}
            onChange={(e) => set("visibility", e.target.checked)}
          />
        </label>
        <label className="universal-sides-desc">
          <span>Description</span>
          <input
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Optional"
          />
        </label>
        <div className="universal-sides-form-actions">
          {editingId && (
            <button type="button" className="universal-sides-btn" onClick={resetForm} disabled={saving}>
              Cancel edit
            </button>
          )}
          <button type="submit" className="universal-sides-btn-primary" disabled={saving}>
            {saving ? "Saving…" : editingId ? "Update side" : "Add universal side"}
          </button>
        </div>
      </form>

      <div className="universal-sides-list-wrap">
        {loading ? (
          <div className="universal-sides-empty">Loading…</div>
        ) : sides.length === 0 ? (
          <div className="universal-sides-empty">No universal sides yet. Add one above.</div>
        ) : (
          <table className="universal-sides-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Price</th>
                <th>Qty</th>
                <th>Visible</th>
                <th>Description</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sides.map((side) => (
                <tr key={side.id} className={editingId === side.id ? "is-editing" : ""}>
                  <td>{side.item}</td>
                  <td>{side.price}</td>
                  <td>{side.quantity}</td>
                  <td>{String(side.visibility) === "0" ? "No" : "Yes"}</td>
                  <td>{side.description}</td>
                  <td className="universal-sides-row-actions">
                    <button type="button" onClick={() => startEdit(side)} disabled={saving}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="danger"
                      onClick={() => remove(side)}
                      disabled={saving}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}
